import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import {
  AccountActionRequest,
  AccountActionType,
  UserLockReason,
} from '../../types/user/userProfile'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'

const SUCCESS_KEYS: Record<AccountActionType, string> = {
  [AccountActionType.LOCK]: 'users:users.success.locked',
  [AccountActionType.UNLOCK]: 'users:users.success.unlocked',
  [AccountActionType.RESET_PASSWORD]: 'users:users.success.password-reset',
}

export function useAccountAction() {
  const { t } = useTranslation(['users', 'exceptions'])
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { authInfo } = useAuth()
  const { poster } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)

  const resetState = () => {
    setLoading(false)
    setError(null)
    setSuccess(null)
  }

  const performAccountAction = async (
    userId: string,
    type: AccountActionType,
    reason?: UserLockReason,
    details?: string
  ): Promise<boolean> => {
    setLoading(true)

    try {
      const request: AccountActionRequest = {
        type,
        ...(type === AccountActionType.LOCK && reason ? { reason } : {}),
        ...(details?.trim() ? { details: details.trim() } : {}),
      }

      await poster(
        `${CONFIG.BOUNCE_API}/api/v1/accounts/${encodeURIComponent(userId)}/actions`,
        request,
        'application/json'
      )
      setError(null)
      setSuccess(t(SUCCESS_KEYS[type]))

      return true
    } catch (caughtError) {
      console.log(caughtError)

      if (caughtError instanceof ApiError) {
        setError(getErrorMessage(caughtError.data))
      } else {
        setError(t('exceptions:unknown'))
      }

      return false
    } finally {
      setLoading(false)
    }
  }

  return { performAccountAction, loading, success, error, resetState }
}
